import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./ReceiptPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function fmtDatetime(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const METHOD_LABEL: Record<string, string> = {
  cash:     "Cash",
  visa:     "Visa",
  instapay: "Instapay",
  other:    "Other",
};

export default function ReceiptPage() {
  const { token } = useParams<{ token: string }>();
  const [data,    setData]    = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/receipts/view/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.error ?? "Receipt not found"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="receipt-page">
      <div style={{ color: "#64748b", marginTop: 80, fontSize: 15 }}>Loading receipt…</div>
    </div>
  );

  if (error || !data) return (
    <div className="receipt-page">
      <div style={{ color: "#dc2626", marginTop: 80, fontSize: 15, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
        {error || "Receipt not found"}
        <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 8 }}>
          This link may be invalid or expired. Please contact the hotel.
        </div>
      </div>
    </div>
  );

  const { hotel, booking, extras, payments, summary } = data;
  const isSettled = summary.balance <= 0;
  const currency  = hotel.currency || "EGP";

  return (
    <div className="receipt-page">
      <div className="receipt-card">
        {/* Hotel header */}
        <div className="receipt-header">
          {hotel.logo && <img src={hotel.logo} alt="Logo" className="receipt-logo" />}
          <div>
            <div className="receipt-hotel-name">{hotel.name}</div>
            <div className="receipt-hotel-contact">
              {hotel.address && <span>{hotel.address}</span>}
              {hotel.phone  && <span> · {hotel.phone}</span>}
              {hotel.email  && <span> · {hotel.email}</span>}
            </div>
          </div>
        </div>

        {/* Status strip */}
        <div className="receipt-status-strip">
          <span className="receipt-status-icon">{isSettled ? "✅" : "⏳"}</span>
          {isSettled ? "Payment Confirmed — Thank you for your stay!" : "Payment Pending"}
        </div>

        <div className="receipt-body">
          {/* Booking details */}
          <div>
            <div className="receipt-section-title">Reservation Details</div>
            <div className="receipt-row">
              <span className="receipt-row-label">Booking Reference</span>
              <span className="receipt-row-value" style={{ fontFamily: "monospace" }}>{booking.bookingId}</span>
            </div>
            <div className="receipt-row">
              <span className="receipt-row-label">Guest Name</span>
              <span className="receipt-row-value">{booking.guestName}</span>
            </div>
            <div className="receipt-row">
              <span className="receipt-row-label">Room</span>
              <span className="receipt-row-value">
                {booking.roomNumber}
                {booking.roomType ? ` — ${booking.roomType}` : ""}
              </span>
            </div>
            <div className="receipt-row">
              <span className="receipt-row-label">Check-in</span>
              <span className="receipt-row-value">
                {fmt(booking.checkIn)} &nbsp;from {hotel.checkInTime}
              </span>
            </div>
            <div className="receipt-row">
              <span className="receipt-row-label">Check-out</span>
              <span className="receipt-row-value">
                {fmt(booking.checkOut)} &nbsp;by {hotel.checkOutTime}
              </span>
            </div>
            <div className="receipt-row">
              <span className="receipt-row-label">Duration</span>
              <span className="receipt-row-value">{booking.nights} night{booking.nights !== 1 ? "s" : ""}</span>
            </div>
          </div>

          {/* Charges */}
          <div>
            <div className="receipt-section-title">Charges</div>
            <div className="receipt-row">
              <span className="receipt-row-label">Room ({booking.nights} night{booking.nights !== 1 ? "s" : ""})</span>
              <span className="receipt-row-value">{currency} {summary.roomTotal.toLocaleString()}</span>
            </div>

            {extras.length > 0 && (
              <>
                <hr className="receipt-divider" />
                {extras.map((e: any, i: number) => (
                  <div key={i} className="receipt-extra-row">
                    <span className="receipt-extra-name">
                      {e.description}
                      {e.qty > 1 ? ` × ${e.qty}` : ""}
                    </span>
                    <span className="receipt-extra-amount">
                      {currency} {(e.amount * (e.qty || 1)).toLocaleString()}
                    </span>
                  </div>
                ))}
              </>
            )}

            <div className="receipt-total-row">
              <span>Total</span>
              <span>{currency} {summary.grandTotal.toLocaleString()}</span>
            </div>
          </div>

          {/* Payments */}
          {payments.length > 0 && (
            <div>
              <div className="receipt-section-title">Payments Received</div>
              {payments.map((p: any, i: number) => (
                <div key={i} className="receipt-payment-row">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="receipt-payment-method">{METHOD_LABEL[p.method] || p.method}</span>
                    {p.note && <span style={{ fontSize: 12, color: "#94a3b8" }}>{p.note}</span>}
                  </div>
                  <div>
                    <span style={{ fontWeight: 600, color: "#15803d" }}>
                      + {currency} {Number(p.amount).toLocaleString()}
                    </span>
                    {p.paidAt && (
                      <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8 }}>
                        {fmtDatetime(p.paidAt)}
                      </span>
                    )}
                  </div>
                </div>
              ))}

              <div className="receipt-total-row">
                <span>Balance Due</span>
                <span className={isSettled ? "receipt-paid-full" : "receipt-balance"}>
                  {isSettled
                    ? `${currency} 0 — Settled`
                    : `${currency} ${summary.balance.toLocaleString()}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="receipt-footer">
          {hotel.name} — {hotel.email || hotel.phone || ""}
          <br />
          This is an official receipt. Thank you for choosing us.
        </div>
      </div>

      <button className="receipt-print-btn" onClick={() => window.print()}>
        Print / Save as PDF
      </button>
    </div>
  );
}
